import { useEffect, useState, useCallback } from "react";
import { motion } from "framer-motion";
import { Crown, Loader2, FolderKanban, TrendingUp, Megaphone, ShoppingBag } from "lucide-react";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid,
  PieChart, Pie, Cell, AreaChart, Area,
} from "recharts";
import OSLayout from "../../components/os/OSLayout";
import { api } from "../../lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";

const ease = [0.16, 1, 0.3, 1];
const VIOLET = "#7c5cff";
const PALETTE = ["#7c5cff", "#a78bfa", "#c4b5fd", "#f59e0b", "#94a3b8"];
const STATUS_LABEL = { nuevo: "Nuevo", en_progreso: "En progreso", en_revision: "En revisión", completado: "Completado", archivado: "Archivado" };
const ALL = "all";

function Card({ title, children, testid, span }) {
  return (
    <div className={`card-surface p-6 ${span || ""}`} data-testid={testid}>
      <div className="overline text-navy/40 mb-4">{title}</div>
      {children}
    </div>
  );
}

export default function ExecutiveDashboard() {
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);
  const [suite, setSuite] = useState(ALL);
  const [initialized, setInitialized] = useState(false);

  const fetchData = useCallback((s) => {
    setLoading(true);
    const params = s && s !== ALL ? { suite: s } : {};
    api.get("/executive/overview", { params }).then((r) => {
      setD(r.data);
      if (!initialized) {
        setInitialized(true);
        if (r.data.default_suite) { setSuite(r.data.default_suite); }
      }
    }).catch(() => {}).finally(() => setLoading(false));
  }, [initialized]);

  useEffect(() => { fetchData(ALL); }, []); // carga inicial
  useEffect(() => { if (initialized) fetchData(suite); }, [suite]); // eslint-disable-line

  const kpis = d ? [
    { label: "Registros totales", value: d.kpis.total }, { label: "Abiertos", value: d.kpis.open },
    { label: "Completados", value: d.kpis.done }, { label: "Prioridad alta", value: d.kpis.high },
    { label: d.scope.suite_id ? "Studios activos" : "Suites activas", value: d.kpis.active_suites },
  ] : [];
  const mods = d ? [
    { label: "Proyectos", value: d.modules.proyectos, icon: FolderKanban },
    { label: "CRM (leads/deals)", value: d.modules.crm, icon: TrendingUp },
    { label: "Campañas", value: d.modules.campanas, icon: Megaphone },
    { label: "Pedidos", value: d.modules.pedidos, icon: ShoppingBag },
  ] : [];
  const statusData = d ? d.by_status.map((s) => ({ name: STATUS_LABEL[s.status] || s.status, value: s.count })).filter((s) => s.value > 0) : [];
  const groupTitle = d?.scope?.suite_id ? "Actividad por Studio" : "Actividad por Suite";

  return (
    <OSLayout title="Panel Ejecutivo">
      <div className="space-y-8" data-testid="executive-dashboard">
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease }} className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <div className="overline text-navy/40 mb-2 flex items-center gap-2"><Crown className="w-3.5 h-3.5" /> OMNIA Executive Suite</div>
            <h2 className="font-editorial text-4xl md:text-5xl tracking-tight">Panel Ejecutivo</h2>
            <p className="mt-2 text-navy/50 max-w-2xl">{d?.scope?.suite_name ? `Enfoque: ${d.scope.suite_name}` : "Visión consolidada con datos agregados de todas las Suites."}</p>
          </div>
          <div className="w-full sm:w-64" data-testid="exec-suite-filter">
            <Select value={suite} onValueChange={setSuite}>
              <SelectTrigger className="field" data-testid="exec-suite-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>Todas las Suites</SelectItem>
                {d?.suites?.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </motion.div>

        {loading ? <div className="py-16 flex justify-center text-navy/40"><Loader2 className="w-6 h-6 animate-spin" /></div> : !d ? (
          <div className="card-surface py-16 text-center text-navy/40 text-sm">No fue posible cargar el panel.</div>
        ) : <>
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-4" data-testid="exec-kpis">
            {kpis.map((k) => (<div key={k.label} className="card-surface p-5"><div className="overline text-navy/40">{k.label}</div><div className="mt-3 text-3xl font-editorial tracking-tight">{k.value}</div></div>))}
          </div>

          <div className="grid gap-6 lg:grid-cols-2">
            <Card title={groupTitle} testid="exec-by-group">
              {d.by_group.length === 0 ? <div className="h-[280px] flex items-center justify-center text-sm text-navy/40">Sin datos aún</div> : (
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={d.by_group} layout="vertical" margin={{ left: 20 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(120,120,140,0.15)" horizontal={false} />
                    <XAxis type="number" tick={{ fontSize: 11, fill: "#94a3b8" }} allowDecimals={false} />
                    <YAxis type="category" dataKey="name" width={110} tick={{ fontSize: 11, fill: "#94a3b8" }} />
                    <Tooltip cursor={{ fill: "rgba(124,92,255,0.08)" }} contentStyle={{ borderRadius: 12, border: "1px solid rgba(120,120,140,0.2)", fontSize: 12 }} />
                    <Bar dataKey="count" fill={VIOLET} radius={[0, 6, 6, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </Card>

            <Card title="Distribución por estado" testid="exec-by-status">
              {statusData.length === 0 ? <div className="h-[280px] flex items-center justify-center text-sm text-navy/40">Sin datos aún</div> : (
                <ResponsiveContainer width="100%" height={280}>
                  <PieChart>
                    <Pie data={statusData} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={60} outerRadius={100} paddingAngle={2}>
                      {statusData.map((_, i) => <Cell key={i} fill={PALETTE[i % PALETTE.length]} />)}
                    </Pie>
                    <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid rgba(120,120,140,0.2)", fontSize: 12 }} />
                  </PieChart>
                </ResponsiveContainer>
              )}
              <div className="flex flex-wrap gap-3 mt-3 justify-center">
                {statusData.map((s, i) => (<span key={s.name} className="text-xs text-navy/50 flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full" style={{ background: PALETTE[i % PALETTE.length] }} />{s.name} ({s.value})</span>))}
              </div>
            </Card>

            <Card title="Tendencia de actividad (6 meses)" testid="exec-activity" span="lg:col-span-2">
              {d.activity.length === 0 ? <div className="h-[220px] flex items-center justify-center text-sm text-navy/40">Sin datos aún</div> : (
                <ResponsiveContainer width="100%" height={220}>
                  <AreaChart data={d.activity} margin={{ left: 4, right: 8 }}>
                    <defs><linearGradient id="ag" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor={VIOLET} stopOpacity={0.35} /><stop offset="100%" stopColor={VIOLET} stopOpacity={0} /></linearGradient></defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(120,120,140,0.15)" />
                    <XAxis dataKey="month" tick={{ fontSize: 11, fill: "#94a3b8" }} />
                    <YAxis tick={{ fontSize: 11, fill: "#94a3b8" }} allowDecimals={false} />
                    <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid rgba(120,120,140,0.2)", fontSize: 12 }} />
                    <Area type="monotone" dataKey="count" stroke={VIOLET} strokeWidth={2} fill="url(#ag)" />
                  </AreaChart>
                </ResponsiveContainer>
              )}
            </Card>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="exec-modules">
            {mods.map((m) => (
              <div key={m.label} className="card-surface p-5 flex items-center gap-4">
                <div className="w-11 h-11 rounded-xl bg-violet/10 flex items-center justify-center"><m.icon className="w-5 h-5 text-violet" /></div>
                <div><div className="text-2xl font-editorial tracking-tight">{m.value}</div><div className="text-xs text-navy/50">{m.label}</div></div>
              </div>
            ))}
          </div>
        </>}
      </div>
    </OSLayout>
  );
}
